import { Box, Button, HStack, Tooltip, useClipboard } from "@chakra-ui/react";
import { t } from "@lingui/macro";
import type { FC, PropsWithChildren } from "react";
import { Icon, type IconProps } from "../Icon";

type Props = {
  valueToCopy: string;
  successText?: string;
  tooltipLabel?: string;
  copyIconName?: IconProps["name"];
};

export const CopyIcon = ({
  valueToCopy,
  copyIconName = "link-03",
  tooltipLabel = t`Copy link`,
  successText = t`Copied!`,
}: Props) => {
  const { hasCopied, onCopy } = useClipboard(valueToCopy);

  return (
    <Tooltip
      label={hasCopied ? successText : tooltipLabel}
      shouldWrapChildren
      placement="top"
      hasArrow
    >
      <Button
        minW="unset"
        h="fit-content"
        minH="unset"
        p="0"
        onClick={onCopy}
        paddingInlineEnd="0"
        paddingInlineStart="0"
        variant="unstyled"
      >
        {hasCopied ? (
          <Icon name="check-circle" color="green.500" />
        ) : (
          <Icon name={copyIconName} w="4" h="4" color="blue.500" />
        )}
      </Button>
    </Tooltip>
  );
};

export const HiddenCopyIcon: FC<
  PropsWithChildren<Props & { extraDataComponentName?: string }>
> = ({
  extraDataComponentName,
  children,
  tooltipLabel = t`Copy link`,
  successText = t`Copied!`,
  valueToCopy,
  copyIconName = "link-03",
}) => {
  const dataComponentName =
    "hide-on-right" +
    (extraDataComponentName ? ` ${extraDataComponentName}` : "");

  return (
    <HStack align="center" data-component-name={dataComponentName} spacing="0">
      {children}
      <Box
        sx={{
          [`[data-component-name="${dataComponentName}"]:hover &`]: {
            opacity: "1",
            ml: "1",
            w: "4",
          },
        }}
        w="0"
        opacity="0"
        transition="all"
        transitionDuration="200ms"
      >
        <CopyIcon
          successText={successText}
          tooltipLabel={tooltipLabel}
          copyIconName={copyIconName}
          valueToCopy={valueToCopy}
        />
      </Box>
    </HStack>
  );
};
